/**
 * The site-wide feedback-offer prompt's rules (owner, 2026-10-15:
 * "people seem to ignore the component in the homepage... build
 * something that would be in other pages and more noisy without
 * immediate chance to close it"; "even if you click later and come
 * back later, it's still going to be shown until you fill it"; "the
 * full-page overlay we show only once with the illustration, the same
 * as in the email, the rest of the time only the bottom right; every
 * session, at the beginning of a session... you're still going to
 * show it").
 *
 * Pure so the rules are testable:
 *   - the first page of a browser session gets the overlay (page dimmed,
 *     the card with the illustration); every later page of the session
 *     gets the corner card;
 *   - either comes up a moment after the page loads, and its "later"
 *     control is not offered until it has been on screen a while;
 *   - once it has come up, the next pages rest for two hours in this
 *     browser (the page where it appeared keeps it until "later"); then
 *     the corner card returns once, and the next day's first page
 *     starts with the overlay again. It stops for good only when the
 *     offer is no longer "open" for the account (answered, granted,
 *     closed); an account under a day old reads "later" from the API
 *     and sees nothing yet;
 *   - it comes up at most 6 times per account, on all devices together
 *     (owner decision 5, 2026-10-29): the API counts the shows and sends
 *     how many are left. The hub card and the e-mail link stay;
 *   - when it may come up at all is the interruption budget's call
 *     (lib/interruption-budget): one offer per session, none on its first
 *     page view, none over a result or a drawer.
 */
export const SHOW_AFTER_MS = 1500;
export const CLOSE_AFTER_MS = 8000;
/** sessionStorage: the overlay has been shown in this browser session
 *  (kept as the fallback where localStorage is not available). */
export const OVERLAY_SEEN_KEY = "menzil_fbo_overlay_seen";
/** localStorage: when the overlay was last shown in this browser. The
 *  per-tab memory alone re-locked every page opened in a new tab (the
 *  owner, 2026-10-15: "scroll doesn't work on any page"), so the
 *  dimmed card now comes once a day per browser; new tabs get the
 *  corner card. */
export const OVERLAY_SEEN_AT_KEY = "menzil_fbo_overlay_seen_at";
export const OVERLAY_REPEAT_MS = 24 * 60 * 60 * 1000;
/** localStorage: when the prompt last came up (or "later" was pressed)
 *  in this browser. It then rests on every other page for a while
 *  (owner, 2026-10-16: "so aggressive... it should have a cooldown, an
 *  hour or two"): the card stays on the page where it appeared and the
 *  next pages are quiet until the rest is over. */
export const REST_AT_KEY = "menzil_fbo_rest_at";
export const REST_MS = 2 * 60 * 60 * 1000;

/** Whether a prompt shown at ``restAtMs`` still rests the next pages now. */
export function resting(restAtMs: number | null, nowMs: number): boolean {
  if (restAtMs === null || !Number.isFinite(restAtMs)) return false;
  if (restAtMs > nowMs) return true; // a clock set back: keep resting
  return nowMs - restAtMs < REST_MS;
}

/** Whether an overlay shown at ``seenAtMs`` still counts as seen now. */
export function overlaySeenRecently(seenAtMs: number | null, nowMs: number): boolean {
  if (seenAtMs === null || !Number.isFinite(seenAtMs)) return false;
  if (seenAtMs > nowMs) return true; // a clock set back: do not show again early
  return nowMs - seenAtMs < OVERLAY_REPEAT_MS;
}

/** Seconds the "later" control still waits, for the countdown it shows
 *  while inert (a dead button read as a frozen page). */
export function laterCountdown(visibleForMs: number): number {
  return Math.max(0, Math.ceil((CLOSE_AFTER_MS - visibleForMs) / 1000));
}

/** Whether the account has had all its shows. The page where the last one
 *  came up keeps its card until "later"; an API that sends no count (an
 *  older backend) caps nothing. */
export function capReached(left: number | null | undefined, upOnThisPage: boolean): boolean {
  if (upOnThisPage || typeof left !== "number" || !Number.isFinite(left)) return false;
  return left <= 0;
}

export type PromptMode = "none" | "overlay" | "corner";

/** Which prompt this page gets. */
export function promptMode(args: {
  state: string | null | undefined;
  overlaySeen: boolean;
  dismissedPath: string | null;
  path: string;
  /** The prompt came up less than REST_MS ago on another page. */
  resting?: boolean;
  /** The account has had all its shows (capReached). */
  capped?: boolean;
}): PromptMode {
  if (args.state !== "open") return "none";
  if (args.resting || args.capped) return "none";
  if (isDismissedFor(args.dismissedPath, args.path)) return "none";
  return args.overlaySeen ? "corner" : "overlay";
}

/** Whether the "later" control may be offered, given how long the card has been visible. */
export function canClosePrompt(visibleForMs: number): boolean {
  return visibleForMs >= CLOSE_AFTER_MS;
}

/** "Later" is per page: a new path re-arms the prompt. */
export function isDismissedFor(dismissedPath: string | null, path: string): boolean {
  return dismissedPath !== null && dismissedPath === path;
}
